import dayjs from "dayjs";
import { JobchainV1_1 } from "~/class/JobchainV1-1";
import { createTableInstance } from "~/class/TableHelper";
import { JobMode } from "~/interface/Job";

/**
 * The two halves of what `usePosJobsStore.computeJobchain` used to do in one
 * function, split so callers can pay only for the half they invalidated.
 *
 *   REPLAY  — sort the whole chain and walk it from job #1 to derive what is on
 *             each table. Expensive: ~3-4us per accumulated order line, so ~25ms
 *             at 1,000 jobs / 6,000 lines and ~42ms at 2,000 jobs.
 *   BILLING — turn that holder into priced Table instances. Cheap: ~2.6ms and
 *             ~7.2ms at the same two sizes.
 *
 * Only a change to the JOBS themselves requires a replay. A change to
 * tablesState (guests / lock / per-table service charge), tableBillingGlobal
 * (shop-wide VAT / service charge) or the split-bill map changes how the SAME
 * items are priced, and needs the billing half only.
 *
 * These live in a plain module rather than inside the store so they can be
 * exercised against real customer jobchains in unit tests — see
 * `__tests__/jobchainRecompute.spec.ts`.
 *
 * NOTE ON INCREMENTAL REPLAY: there isn't one, on purpose. A second cashier
 * writes its own jobs and they arrive out of chronological order, so every job
 * forces a full re-sort and a full replay from the head of the chain. Do not
 * "optimise" this into an append — see the sort-key comment below.
 */

export interface BillingContext {
    /** Shop-wide billing defaults (VAT / service charge / rounding). */
    tableBillingGlobal: any;
    /** Per-table overrides + guests, keyed by table alias. */
    tablesState: Record<string, any>;
    /** TO-GO bills take no service charge. */
    togoNoService?: boolean;
    /** Resolves the static "no-discount" tag from a sub-item id. */
    noDiscountResolver?: (subItemId: any) => boolean;
    /**
     * Resolves the divana "Product" (VAT-inclusive, service-exempt) tag from a
     * sub-item id. Like noDiscountResolver it is a static per-sub-item property
     * re-derived at build time, so it belongs to the billing half — only
     * DivanaBillingStrategy reads the resulting `skipService` flag.
     */
    skipServiceResolver?: (subItemId: any) => boolean;
    /** Split-bill assignment, keyed by cart-line uuid. */
    uuidItemToBillNumber?: Record<string, any>;
}

/**
 * Replay order key. Prefer pcTimestamp (the cashier PC's receive time, stamped
 * on ingest for mobile orders) over timestamp (the device clock, which can be
 * wrong on customer phones). This keeps a late mobile ADD from replaying before
 * a PAY and resurrecting paid items. OPEN_SHOP keeps the earliest time, so it
 * stays at index 0.
 */
export const jobSortKey = (job: any) => job?.pcTimestamp || job?.timestamp;

export const sortJobs = (jobs: Record<string, any> | any[]): any[] =>
    (Array.isArray(jobs) ? [...jobs] : (Object.values(jobs || {}) as any[]))
        .sort((a, b) => dayjs(jobSortKey(a)).diff(dayjs(jobSortKey(b))));

/**
 * EXPENSIVE HALF. Walks the entire chain and returns the JobchainV1_1 instance
 * holding the derived table state. Keep the instance, not just its return value:
 * `buildTableInstances` needs it to re-apply split-bill numbers.
 *
 * Deliberately takes no billing input. `computeAllBlocks` only uses
 * uuidItemToBillNumber in its final `setBill()` pass, which `buildTableInstances`
 * re-runs anyway — so leaving it out here keeps the split honest (a split-bill
 * edit must never be able to trigger a replay) without changing the result.
 */
export function replayJobchain(jobs: Record<string, any> | any[]): JobchainV1_1 {
    const jobchain = new JobchainV1_1();
    jobchain.computeAllBlocks(sortJobs(jobs));
    return jobchain;
}

/**
 * CHEAP HALF. Prices the already-derived tables. Pure with respect to the
 * jobchain: it reads `tableHolder` and returns fresh Table instances without
 * mutating the items it read (calculateItems maps to new objects), so it is safe
 * to run repeatedly against one replay.
 */
export function buildTableInstances(
    jobchain: JobchainV1_1,
    ctx: BillingContext
): Record<string, any> {
    // Re-applied on every billing pass, not just after a replay: setBill is total
    // (it assigns 1 when a line has no split-bill entry), so clearing a split
    // correctly puts the line back on bill 1 without needing the chain replayed.
    jobchain.setBill(ctx.uuidItemToBillNumber || {});

    const tables: Record<string, any> = {};
    const holder = jobchain.tableHolder || {};
    for (const key in holder) {
        const table = holder[key];
        tables[key] = createTableInstance(
            table,
            ctx.tableBillingGlobal,
            ctx.tablesState?.[key],
            {
                // togoNoService: บิล TO-GO ไม่คิดค่าบริการ — บังคับตอนคิดบิลของการ์ดโต๊ะทุกใบ
                noServiceCharge: table.mode === JobMode.TOGO && !!ctx.togoNoService,
                noDiscountResolver: ctx.noDiscountResolver,
                skipServiceResolver: ctx.skipServiceResolver,
            }
        );
    }
    return tables;
}

/** Both halves. Used at boot, and by tests as the reference result. */
export function computeTables(
    jobs: Record<string, any> | any[],
    ctx: BillingContext
): Record<string, any> {
    return buildTableInstances(replayJobchain(jobs), ctx);
}
